import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IS_PROD_ZONE } from '@/constants/env';
import { cn } from '@/utils/style';

import HospitalIcon from '@/shared/icons/HospitalIcon';
// 아이콘 import 추가
import NaverIdLoginIcon from '@/shared/icons/naveridlogin.svg';
import NPayIcon from '@/shared/icons/npay.svg';
import NPayPlusIcon from '@/shared/icons/npay_plus.svg';
import TalkTalkIcon from '@/shared/icons/talktalk.svg';
import NaverAdSkeleton from './NaverAdSkeleton';

// 네이버 파워링크 API 응답 타입 (개발 가이드 기준)
interface NaverAdApiResponse {
  ads: NaverAd[];
}

interface NaverAd {
  headline: string;
  description: string;
  displayUrl: string;
  clickUrl: string;

  // 네이버 아이콘들
  naverPayIconType: number;
  isNaverLoginIconEnabled: boolean;
  isTalkTalkIconEnabled: boolean;

  // 의료 광고 관련 필드
  medicalNo?: string;
  adCreativeType?: number;
  tags?: string[];
  mainImageUrls?: string[];
  faviconImageUrl?: string;

  // 파워링크 이미지 확장소재 (Group A)
  imageExtension?: {
    imageUrl: string;
    clickUrl: string;
  };

  // 서브링크 확장소재 (Group B)
  subLinkExtension?: Array<{
    name: string;
    clickUrl: string;
  }>;

  // 홍보문구 확장소재 (Group B)
  descriptionExtension?: {
    heading: string;
    description: string;
    clickUrl: string;
  };

  // 플레이스정보 확장소재 (Group B)
  placeExtension?: {
    minimumPrice?: string;
    openingHours?: string[];
    promotionTitle?: string;
    reviewCount?: number;
    recentBookingCount?: number;
    averagePrice?: string;
    menuPrice?: string;
    clickUrl: string;
  };

  // 추가제목 확장소재
  extraHeadlineExtensions?: Array<{
    headline: string;
  }>;

  // 추가설명 확장소재
  extraDescriptionExtension?: {
    description: string;
  };

  // 이미지형 서브링크 확장소재 (모바일에서만 사용)
  imageSubLinksExtension?: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
}

interface NaverAdComponentProps {
  channel: string;
  title: string;
  content: string;
  keywordGroup?: string;
  url?: string;
  test?: boolean;
  className?: string;
  style?: React.CSSProperties;
  pageSize?: number;
}

// 아이콘 선택 함수 추가 (네이버 서비스 아이콘들)
const getNaverIcon = (ad: NaverAd) => {
  // 우선순위: 페이 > 로그인 > 톡톡
  if (ad.naverPayIconType === 2) {
    return <NPayPlusIcon className='h-[16px] w-auto' />;
  }
  if (ad.naverPayIconType === 1) {
    return <NPayIcon className='h-[16px] w-auto' />;
  }
  if (ad.isNaverLoginIconEnabled) {
    return <NaverIdLoginIcon className='h-[16px] w-auto' />;
  }
  if (ad.isTalkTalkIconEnabled) {
    return <TalkTalkIcon className='h-[16px] w-auto' />;
  }
  return null;
};

// 의료 광고 여부 확인 함수
const isMedicalAd = (ad: NaverAd): boolean => {
  return Boolean(ad.medicalNo);
};

export const NaverAdComponent: React.FC<NaverAdComponentProps> = ({
  channel,
  title,
  content,
  keywordGroup = '모바일닥터나우_공통',
  url,
  test = !IS_PROD_ZONE,
  className = '',
  style = {},
  pageSize = 2,
}) => {
  const [adData, setAdData] = useState<NaverAd[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const scriptLoadingRef = useRef(false);

  const handleNaverAdsResponse = useCallback((response: NaverAdApiResponse) => {
    try {
      if (response && response.ads && response.ads.length > 0) {
        setAdData(response.ads);
      } else {
        setAdData([]);
      }
    } catch (error) {
      console.error('Naver ads response error:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 콜백 함수 등록 (항상 실행)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.handle_naver_ads_response = handleNaverAdsResponse;

    return () => {
      if (window.handle_naver_ads_response) {
        delete window.handle_naver_ads_response;
      }
    };
  }, [handleNaverAdsResponse]);

  // 스크립트 로딩 (한 번만)
  useEffect(() => {
    if (typeof window === 'undefined' || scriptLoaded || scriptLoadingRef.current) {
      return;
    }

    scriptLoadingRef.current = true;

    const loadScript = () => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://ssl.pstatic.net/adimg3.search/adpost/js/adpost_show_ads_v2.min.js';
      script.async = true;

      script.onload = () => {
        setScriptLoaded(true);
        scriptLoadingRef.current = false; // 로딩 완료 후 false로 재설정
      };

      script.onerror = () => {
        setHasError(true);
        setIsLoading(false);
        scriptLoadingRef.current = false; // 로딩 실패 후에도 false로 재설정
      };

      document.head.appendChild(script);
    };

    loadScript();
  }, [scriptLoaded]);

  // 광고 API 호출 (스크립트 로드 완료 후 & props 변경 시마다)
  useEffect(() => {
    if (typeof window === 'undefined' || !scriptLoaded) {
      return;
    }

    // props 변경 시 로딩 상태 초기화
    setIsLoading(true);
    setHasError(false);
    setAdData([]);

    if (window.NAVER_ADPOST_V2) {
      try {
        const adParams = {
          channel,
          pageSize,
          title: title.substring(0, 100), // 100 bytes 제한
          content: content.substring(0, 1000), // 1000 bytes 제한
          keywordGroup,
          url: url || window.location.href,
          test,
        };

        window.NAVER_ADPOST_V2(adParams);
      } catch (error) {
        console.error('❌ 네이버 광고 API 호출 에러:', error);
        setHasError(true);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [scriptLoaded, channel, title, content, keywordGroup, url, test, pageSize]);

  if (hasError || (!isLoading && !adData.length)) {
    return null;
  }

  if (isLoading) {
    return <NaverAdSkeleton className={className} style={style} />;
  }

  return (
    <div className={cn('p-[20px] transition-opacity duration-300 ease-in-out', className)} style={style}>
      <div className='flex min-h-[140px] flex-col gap-[12px] overflow-hidden rounded-lg border border-gray-200 border-solid bg-white p-[12px]'>
        <div className='flex items-center justify-between border-gray-100 border-b'>
          <div className='flex items-center gap-[4px]'>
            <span className='typography-ui-14b text-gray-600'>파워링크</span>
            <span className='typography-ui-11r rounded-[4px] border border-gray-100 border-solid px-[2px] text-gray-500'>
              광고
            </span>
          </div>
          <a
            href='https://ads.naver.com/sa'
            target='_blank'
            rel='noopener noreferrer'
            className='!underline text-gray-500 text-xs underline-offset-2'
          >
            광고등록
          </a>
        </div>

        <div className='flex flex-col gap-[12px]'>
          {adData.map((ad, index) => {
            const naverIcon = getNaverIcon(ad);
            const isAdMedical = isMedicalAd(ad);

            return (
              <a
                key={`${ad.headline}-${index}`}
                href={ad.clickUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='block cursor-pointer'
              >
                <div className='flex items-start gap-[12px]'>
                  <div className='min-w-0 flex-1'>
                    {/* adCreativeType !== 15일 때 displayUrl을 제목 상단에 표시 (UI 통일성) */}
                    {ad.adCreativeType !== 15 && (
                      <div className='mb-[4px] flex items-center gap-[6px]'>
                        <span className='typography-ui-12m truncate text-gray-400'>{ad.displayUrl}</span>
                        {naverIcon && <div className='flex-shrink-0'>{naverIcon}</div>}
                      </div>
                    )}

                    {/* adCreativeType=15일 때 파비콘과 제목을 함께 표시 */}
                    <div className='mb-[4px] flex items-center gap-[6px]'>
                      {/* 의료 광고 파비콘 표시 (adCreativeType=15일 때만 제목 앞에) */}
                      {isAdMedical && ad.adCreativeType === 15 && (
                        <div className='flex h-[16px] w-[16px] flex-shrink-0 items-center justify-center'>
                          {ad.faviconImageUrl ? (
                            <img
                              src={ad.faviconImageUrl}
                              alt='파비콘'
                              className='h-[16px] w-[16px]'
                              width={16}
                              height={16}
                              loading='lazy'
                            />
                          ) : (
                            <HospitalIcon className='h-[16px] w-[16px]' />
                          )}
                        </div>
                      )}
                      <span className='typography-ui-14b !underline text-blue-700 leading-tight underline-offset-2'>
                        {ad.headline}
                      </span>
                      {/* adCreativeType=15일 때 네이버 아이콘을 제목 뒤에 */}
                      {ad.adCreativeType === 15 && naverIcon && <div className='flex-shrink-0'>{naverIcon}</div>}
                    </div>

                    <div
                      className={cn([
                        'flex desktop:flex-row flex-col desktop:items-center gap-[4px]',
                        {
                          'inline-block': ad.adCreativeType === 15,
                        },
                      ])}
                    >
                      <p
                        className={cn([
                          'typography-ui-12m line-clamp-2 inline-block cursor-pointer break-all text-gray-400',
                          {
                            inline: ad.adCreativeType === 15,
                          },
                        ])}
                      >
                        {ad.description}
                      </p>

                      {/* 의료 광고일 때 의료심의번호 표시 */}
                      {isAdMedical && ad.medicalNo && (
                        <div
                          className={cn([
                            'typography-ui-11m mt-[8px] text-gray-300',
                            'desktop:mt-[0px]',
                            {
                              inline: ad.adCreativeType === 15,
                            },
                          ])}
                        >
                          <span
                            className={cn([
                              'desktop:inline hidden pr-[4px]',
                              {
                                'inline pl-[4px]': ad.adCreativeType === 15,
                              },
                            ])}
                          >
                            ·
                          </span>
                          {ad.medicalNo}
                        </div>
                      )}
                    </div>

                    {/* 의료 광고 태그 표시 (adCreativeType=15일 때) */}
                    {isAdMedical && ad.adCreativeType === 15 && ad.tags && ad.tags.length > 0 && (
                      <div className='my-[8px] flex flex-wrap gap-[4px]'>
                        {ad.tags.slice(0, 6).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className='typography-ui-11m rounded-[4px] bg-gray-50 px-[6px] py-[2px] text-gray-600'
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* 의료 광고 서브링크 표시 */}
                    {isAdMedical && ad.subLinkExtension && ad.subLinkExtension.length > 0 && (
                      <div className='my-[8px] flex flex-wrap gap-[4px]'>
                        {ad.subLinkExtension.map((subLink, subIndex) => (
                          <button
                            key={subIndex}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(subLink.clickUrl, '_blank', 'noopener,noreferrer');
                            }}
                            className='typography-ui-11m cursor-pointer rounded-full border border-gray-100 border-solid px-[8px] py-[2px] text-blue-600 hover:bg-blue-50'
                          >
                            {subLink.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 이미지 확장소재 또는 메인 이미지 표시 */}
                  {(ad.imageExtension || (ad.mainImageUrls && ad.mainImageUrls.length > 0)) && (
                    <div className='flex-shrink-0'>
                      {ad.imageExtension ? (
                        <div
                          className='cursor-pointer'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (ad.imageExtension) {
                              window.open(ad.imageExtension.clickUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          <img
                            src={ad.imageExtension.imageUrl}
                            alt='광고 이미지'
                            className='h-[60px] w-[60px] rounded-[8px] bg-gray-100 object-cover transition-opacity duration-200'
                            width={60}
                            height={60}
                            loading='lazy'
                          />
                        </div>
                      ) : (
                        ad.mainImageUrls &&
                        ad.mainImageUrls[0] && (
                          <img
                            src={ad.mainImageUrls[0]}
                            alt='메인 이미지'
                            className='h-[60px] w-[60px] rounded-[8px] bg-gray-100 object-cover transition-opacity duration-200'
                            width={60}
                            height={60}
                            loading='lazy'
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NaverAdComponent;
